/* POVA — field interaction
 * Cursor-driven halo + live spec ticker.
 *
 * Listens for [data-product] hover anywhere in the page. On hover:
 *   - sets --field-halo-color and --field-halo-strength on :root
 *   - streams that product's spec into the .ticker element
 *   - updates .comp-number to that product's composition index
 * Cursor position drives --field-halo-x / --field-halo-y so the halo
 * tracks the mouse smoothly (no transition on position — eases via lerp).
 *
 * Colour resolution (so EVERY product tints, on every page, at the same
 * intensity):
 *   1. window.POVA_PRODUCTS registry, keyed by the element's data-product id.
 *   2. If the id isn't in the registry, the element's own data-halo (+ the
 *      optional data-halo-name / data-halo-tag / data-halo-index) — the same
 *      per-element pattern the /pages/products catalogue uses. This lets any
 *      page opt a product in without widening the shared registry, and keeps
 *      the four launch SKUs (whose live handles differ from the registry ids)
 *      tinting identically to the lineage models. */

(function () {
  const root = document.documentElement;
  const ticker = document.querySelector(".ticker");
  const compNumber = document.querySelector(".comp-number");
  const products = window.POVA_PRODUCTS || [];
  const productMap = Object.fromEntries(products.map(p => [p.id, p]));

  /* Reduced motion (WCAG 2.1 §2.3.3): the halo's rAF follow-loop is purely
   * decorative continuous motion. js-cursor.js already gates its own loop
   * the same way — mirror that here instead of animating regardless. */
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* Family size = shipping SKUs (4) — POVA 8, 7 Pro, Curve, Curve 2.
   * POVA_PRODUCTS contains 5 entries because pova-8 has two color variants;
   * the constant decouples the chrome denominator from variant count. */
  const POVA_FAMILY_SIZE = 10;
  const DEFAULT_HALO = "#FF5D01";                 /* orange */
  /* Page-default ticker text. Pages set their own static string in HTML;
   * this is the fallback the field uses when a hover engagement disengages
   * and there's no captured page-default. Kept generic — page-specific
   * identity (e.g. "A1 / ABOUT / DESIGNED AND BUILT TO TOLERANCE") is set in
   * each page's own HTML and captured below. */
  const DEFAULT_TICKER_TEXT = "00 / FIELD / 2026";
  /* Touch devices need a much softer halo — at desktop strength the
   * tinted radial overwhelms the small viewport and reads as a
   * fullscreen colour wash on tap. */
  const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const ENGAGE_STRENGTH = isCoarsePointer ? "0.32" : "0.65";
  /* Resting (ambient) halo — the faint orange that identifies the field before
   * any hover. Bumped up so it's identifiable at rest (was 0.12/0.06). */
  const AMBIENT_STRENGTH = isCoarsePointer ? "0.11" : "0.20";

  /* Each page sets its own static comp string in the HTML comp-number node.
   * On load we capture the static markup as the page-default and ALWAYS
   * restore it on disengage. Engage will not overwrite a page that doesn't
   * carry a [data-product] hover affordance. */
  const compDefaultHTML =
    compNumber
      ? (compNumber.getAttribute("data-comp-default") || compNumber.innerHTML.trim())
      : "";
  if (compNumber && compDefaultHTML && !compNumber.getAttribute("data-comp-default")) {
    compNumber.setAttribute("data-comp-default", compDefaultHTML);
  }
  /* Capture each page's page-static ticker text so disengage restores it
   * (instead of overwriting every page with the constant DEFAULT_TICKER_TEXT
   * — which used to leak the brand tagline across non-home pages). */
  const tickerDefaultText =
    ticker && ticker.textContent.trim()
      ? ticker.textContent.trim()
      : DEFAULT_TICKER_TEXT;
  /* Whether this page should react to product hover (PDPs preserve their
   * static "08 / 04" string and do not flip on hover; only home + products
   * pages — which carry hover affordances — flip during interaction). */
  const isInteractiveCompPage = !!document.querySelector(".instrument, .product-card, .constellation .node");

  /* Mouse-tracked halo position — no transition, just lerped follow.
   * Color and strength are @property-registered and transition via CSS. */
  let targetX = 50, targetY = 50;     /* viewport %, 0–100 */
  let currentX = 50, currentY = 50;

  window.addEventListener("mousemove", (e) => {
    targetX = (e.clientX / window.innerWidth) * 100;
    targetY = (e.clientY / window.innerHeight) * 100;
  });

  /* Touch parity — finger-tracked halo on touch devices. Same code path. */
  window.addEventListener("touchmove", (e) => {
    const t = e.touches[0];
    if (!t) return;
    targetX = (t.clientX / window.innerWidth) * 100;
    targetY = (t.clientY / window.innerHeight) * 100;
  }, { passive: true });
  window.addEventListener("touchstart", (e) => {
    const t = e.touches[0];
    if (!t) return;
    targetX = (t.clientX / window.innerWidth) * 100;
    targetY = (t.clientY / window.innerHeight) * 100;
  }, { passive: true });

  function tick() {
    currentX += (targetX - currentX) * 0.18;
    currentY += (targetY - currentY) * 0.18;
    root.style.setProperty("--field-halo-x", currentX + "%");
    root.style.setProperty("--field-halo-y", currentY + "%");
    requestAnimationFrame(tick);
  }
  if (reduceMotion) {
    /* No continuous follow loop — paint the halo at a fixed centred
     * position once instead of chasing the pointer every frame. */
    root.style.setProperty("--field-halo-x", currentX + "%");
    root.style.setProperty("--field-halo-y", currentY + "%");
  } else {
    requestAnimationFrame(tick);
  }

  /* Hover to engage halo + stream ticker */
  let activeId = null;

  function engage(id, el) {
    if (id === activeId) return;
    activeId = id;

    const p = productMap[id];
    /* Colour: registry first, then the element's own data-halo. If neither
     * supplies a colour, fall back to ambient (never leave a dead hover). */
    const color = (p && p.halo) || (el && el.dataset && el.dataset.halo) || null;
    if (!color) return resetField();

    root.style.setProperty("--field-halo-color", color);
    root.style.setProperty("--field-halo-strength", ENGAGE_STRENGTH);

    /* Identity for the ticker + comp number — registry entry if we have one,
     * otherwise the element's data-halo-* attributes (both optional). */
    const name  = p ? p.name    : (el && el.dataset ? el.dataset.haloName  : "");
    const tag   = p ? p.tagline : (el && el.dataset ? el.dataset.haloTag   : "");
    const index = p ? p.index   : (el && el.dataset ? el.dataset.haloIndex : "");

    /* Ticker streams the product's verified campaign tagline (per
     * brand book 02_Verbal_Identity.md §6), not the spec sheet. */
    if (ticker && name) {
      const idx = index ? index + " / " : "";
      const tg  = tag ? " — " + tag.toUpperCase() : "";
      ticker.textContent = `${idx}${name.toUpperCase()}${tg}`;
      ticker.dataset.streaming = "true";
    }

    /* Composition number shifts to this product — but ONLY on pages that
     * are interactive (home, products). PDPs keep their static `08 / 04`
     * string so the corner number never lies about the family size. */
    if (compNumber && isInteractiveCompPage && index) {
      const total = String(POVA_FAMILY_SIZE).padStart(2, "0");
      compNumber.innerHTML = `${index} <span class="dim">/ ${total}</span>`;
    }
  }

  function disengage(id) {
    if (id !== activeId) return;
    resetField();
  }

  function resetField() {
    activeId = null;
    root.style.setProperty("--field-halo-color", DEFAULT_HALO);
    /* Faint ambient — softer on touch devices so the page doesn't read
     * as a constant orange wash on small mobile viewports. */
    root.style.setProperty("--field-halo-strength", AMBIENT_STRENGTH);
    if (ticker) {
      ticker.textContent = tickerDefaultText;
      ticker.dataset.streaming = "false";
    }
    /* Restore the page-static comp string. Read the attribute live so
     * a phase-aware caller (e.g. motion.js's home phase tracker) can
     * mutate the default mid-session and we'll honour it on disengage,
     * rather than reverting to the value captured at load time. */
    if (compNumber) {
      const live = compNumber.getAttribute("data-comp-default") || compDefaultHTML;
      if (live) compNumber.innerHTML = live;
    }
  }

  /* Initialize default ticker if the page didn't pre-populate one */
  if (ticker && !ticker.textContent.trim()) ticker.textContent = DEFAULT_TICKER_TEXT;
  /* Comp-number is page-static — never inject runtime "00 / 04" if a page
   * left it empty (it shouldn't); leave whatever the HTML supplied. */

  /* Delegate hover events */
  document.addEventListener("mouseover", (e) => {
    const el = e.target.closest("[data-product]");
    if (el) engage(el.dataset.product, el);
  });
  document.addEventListener("mouseout", (e) => {
    const el = e.target.closest("[data-product]");
    if (!el) return;
    /* only disengage if mouse left this product entirely */
    if (e.relatedTarget && el.contains(e.relatedTarget)) return;
    disengage(el.dataset.product);
  });

  /* Paint the resting ambient halo immediately on load so the field's orange
   * signal is identifiable before the first hover. Skipped on product pages
   * (PDPs), where the resting halo is the selected finish colour set by the
   * page's own halo script + css-pdp-halo, not the ambient orange. */
  if (!document.body.classList.contains("product")) {
    root.style.setProperty("--field-halo-color", DEFAULT_HALO);
    root.style.setProperty("--field-halo-strength", AMBIENT_STRENGTH);
  }

  /* On product page, expose engage(id) so the page can lock to its product */
  window.POVA_FIELD = { engage, disengage, resetField };
})();
